import { SubmitHandler, useForm } from "react-hook-form";
import { useRouter } from "next/router";
import { useState } from "react";
import ReactDOMServer from "react-dom/server";
import useTranslation from "next-translate/useTranslation";

import {
    ARROW_RIGHT,
    CHECK,
    CLOCK,
    CLOSE,
    Icon,
    MAP_PIN,
    USER
} from "components/ui/icon";
import {
    Buttons,
    CloseModalButton,
    FixedButtonWrapper,
    FormErrorButton,
    FormErrorWrapper,
    FormInputsWrapper,
    IconWrapper,
    InfoLabelsWrapper,
    RequiredText,
    StyledInput,
    StyledLabel,
    StyledSubmitButton,
    StyledTextArea,
    StyledVacancyForm,
    SuccessBlock,
    SuccessIcon,
    SuccessTitle,
    TagItem,
    TagItemIcon
} from "components/shared/vacancy/vacancyForm/VacancyForm.styled";
import { FileUpload } from "components/shared/fileUpload/FileUpload";
import { Heading } from "components/ui";

import { fileListToBase64 } from "helpers/transform/base64";
import { useMediaQueryContext } from "helpers/hooks";

type FileAttachment = {
    base64: string;
    filename: string;
};

export type VancancyFormProps = {
    jobTitle: string | null;
    label?: string | null;
    hours?: string;
    jobEducationLevel?: string | null;
    jobLocation?: string;
    jobId?: string | null;
    closeModal?: () => void;
    isZZP?: boolean;
};

interface FormInputFields {
    firstName: string;
    id: string | number;
    lastName: string;
    email: string;
    phoneNumber: string;
    message: string;
    platform?: string;
    vacancyFormFiles: Array<File>;
}

const defaultSubmitErrorText =
    "Er is iets mis gegaan bij het versturen van het formulier, probeer het later nog eens.";

function VacancyForm({
    jobTitle,
    label,
    hours,
    jobId,
    jobEducationLevel,
    jobLocation,
    isZZP,
    closeModal
}: VancancyFormProps): JSX.Element {
    const router = useRouter();
    const { isMobileView } = useMediaQueryContext();

    const [submitErrorText, setSubmitErrorText] = useState(
        defaultSubmitErrorText
    );
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [hasErrorInForm, setHasErrorInForm] = useState(false);
    const [isFormLoading, setIsFormLoading] = useState(false);
    const [submitterData, setSubmitterData] = useState<{
        name: string;
        email: string;
    }>();
    const [submitting, setSubmitting] = useState(false);

    const { t } = useTranslation("common");

    const {
        handleSubmit,
        register,
        setValue,
        watch,
        reset,
        formState: { errors }
    } = useForm<FormInputFields>();

    // eslint-disable-next-line
    const onSubmit: SubmitHandler<any> = async function (
        data: FormInputFields
    ) {
        setSubmitting(true);
        const fileAttachments: Array<File> = [];

        Object.entries(data).forEach(entry => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (Array.isArray(entry[1])) {
                entry[1].forEach(file => {
                    if (file instanceof File) {
                        fileAttachments.push(file);
                    }
                });
            }
        });

        const arrayOfBase64 = await fileListToBase64(fileAttachments);

        const utmSource = router.query.utm_source;

        let response;

        if (isZZP) {
            // eslint-disable-next-line
            const emailBodyHTML = ReactDOMServer.renderToStaticMarkup(
                <div>
                    <b>Naam:</b> {data.firstName} {data.lastName}
                    <br />
                    <br />
                    <b>Email:</b> {data.email}
                    <br />
                    <br />
                    <b>Telefoonnummer:</b> {data.phoneNumber}
                    <br />
                    <br />
                    {data.message && (
                        <div>
                            <b>Bericht:</b>
                            <br />
                            <p>{data.message}</p>
                            <br />
                        </div>
                    )}
                    {utmSource && (
                        <div>
                            <b>utm_source:</b>
                            <br />
                            <p>{utmSource}</p>
                        </div>
                    )}
                </div>
            );

            response = await fetch(`/api/mail`, {
                body: JSON.stringify({
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    attachment: arrayOfBase64.map((item: FileAttachment) => {
                        return { data: item.base64, filename: item.filename };
                    }),
                    from: "Noreply <noreply@leadhealthcare.nl>",
                    message: emailBodyHTML,
                    subject: `Sollicitatie - ${
                        jobTitle ?? "Geen vacature titel"
                    } - ${data.firstName} ${data.lastName}`,
                    to: "marketing@leadhealthcare.nl"
                }),
                credentials: "same-origin",
                headers: {
                    "Content-Type": "application/json"
                },
                method: "POST"
            });
        } else {
            // Map the base64 strings to the correct format
            // First file is the CV and the second file is the motivation letter
            const [cv, motivation] = arrayOfBase64.map(file => ({
                base64: file.base64.split("base64,")[1],
                filename: file.filename
            }));

            response = await fetch("/api/post-application", {
                body: JSON.stringify({
                    cv,
                    email: data.email,
                    firstName: data.firstName,
                    jobId,
                    lastName: data.lastName,
                    message: data.message,
                    motivation,
                    phone: data.phoneNumber,
                    utmSource
                }),
                method: "POST"
            });
        }

        if (response.status === 200 || response.status === 201) {
            reset();
            setSubmitterData({ email: data.email, name: data.firstName });
            setIsFormSubmitted(true);

            if (typeof window !== "undefined") {
                window?.dataLayer?.push({
                    data: {
                        department: label ?? "N/A",
                        email: data.email,
                        form_id: Buffer.from(label ?? "N/A").toString("base64"),
                        name: `${data.firstName} ${data.lastName}`,
                        phone: data.phoneNumber ?? "N/A",
                        region: jobLocation ?? "N/A",
                        title: jobTitle ?? "N/A"
                    },
                    event: "form_submit_sollicitatie"
                });
            }
        } else {
            response.json().then((data: { message: string }) => {
                if (data.message) {
                    setSubmitErrorText(data.message);
                }
            });
            setHasErrorInForm(true);
        }
        setIsFormLoading(false);
        setSubmitting(false);
    };

    /* Registering the textarea and fileupload separately */
    register("message");
    register("vacancyFormFiles");

    const formErrorBlock = (
        <FormErrorWrapper>
            {submitErrorText}
            <FormErrorButton
                buttonType="primary"
                onClick={() => setHasErrorInForm(false)}
            >
                Opnieuw proberen
            </FormErrorButton>
        </FormErrorWrapper>
    );

    const formSuccessBlock = (
        <>
            <SuccessBlock id="gtm-form-submitted">
                <SuccessTitle>
                    <SuccessIcon
                        icon={CHECK}
                        size={30}
                        color="var(--color-green-50)"
                    />
                    Je sollicatie is verstuurd
                    {submitterData && submitterData.name
                        ? `, ${submitterData.name}!`
                        : "!"}
                </SuccessTitle>
                Gelukt! Jouw sollicitatie{" "}
                {jobTitle && <b>{`${`op ${jobTitle} `}`}</b>}
                is succesvol verstuurd. Je ontvangt een bevestiging
                {submitterData && submitterData.email ? (
                    <b>{` op ${submitterData.email}. `}</b>
                ) : (
                    " via de mail. "
                )}
                Wij proberen je binnen twee werkdagen van reactie te voorzien!
            </SuccessBlock>
            {closeModal && (
                <FixedButtonWrapper>
                    <CloseModalButton
                        onClick={() => closeModal()}
                        buttonType="ghost"
                        icon={CLOSE}
                        reversedOrder
                    >
                        Venster sluiten
                    </CloseModalButton>
                </FixedButtonWrapper>
            )}
        </>
    );

    return (
        <StyledVacancyForm>
            <Heading headingLevel={3}>Solliciteer voor {jobTitle}</Heading>
            <InfoLabelsWrapper>
                {label && <StyledLabel>{label}</StyledLabel>}
                {hours && (
                    <TagItem>
                        <TagItemIcon
                            icon={CLOCK}
                            size={24}
                            color="var(--color-primary)"
                        />
                        {hours}
                    </TagItem>
                )}

                {jobEducationLevel && (
                    <TagItem>
                        <TagItemIcon
                            icon={USER}
                            size={24}
                            color="var(--color-primary)"
                        />
                        {jobEducationLevel}
                    </TagItem>
                )}
                {jobLocation && (
                    <TagItem>
                        <TagItemIcon
                            icon={MAP_PIN}
                            size={24}
                            color="var(--color-primary)"
                        />
                        {jobLocation}
                    </TagItem>
                )}
            </InfoLabelsWrapper>
            {hasErrorInForm ? (
                formErrorBlock
            ) : isFormSubmitted ? (
                formSuccessBlock
            ) : (
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                <form onSubmit={handleSubmit(onSubmit)}>
                    <FormInputsWrapper>
                        <StyledInput
                            label="Voornaam *"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            errorMessage={errors.firstName?.message}
                            {...register("firstName", {
                                required: {
                                    message: "Vul een voornaam in",
                                    value: true
                                }
                            })}
                            placeholder="Vul hier je voornaam in"
                        />
                        <StyledInput
                            label="Achternaam *"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            errorMessage={errors.lastName?.message}
                            {...register("lastName", {
                                required: {
                                    message: "Vul een voornaam in",
                                    value: true
                                }
                            })}
                            placeholder="Vul hier je achternaam in"
                        />
                        <StyledInput
                            label="E-mailadres *"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            errorMessage={errors.email?.message}
                            {...register("email", {
                                pattern: {
                                    message: "Vul een geldig emailadres in",
                                    value: /[a-z0-9]+@[a-z]+\.[a-z]{2,3}/
                                },
                                required: {
                                    message: "Vul een emailadres in",
                                    value: true
                                }
                            })}
                            placeholder="Vul hier je e-mailadres in"
                        />
                        <StyledInput
                            label="Telefoonnummer *"
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            errorMessage={errors.phoneNumber?.message}
                            {...register("phoneNumber", {
                                pattern: {
                                    message: "Vul een geldig telefoonnummer in",
                                    value: /\+?\d+/
                                },
                                required: {
                                    message: "Vul een telefoonnummer in",
                                    value: true
                                }
                            })}
                            placeholder="Vul hier je telefoonnummer in"
                        />
                        <FileUpload
                            label="CV / Motivatie"
                            subLabel="(Word / PDF, max. 20 MB)"
                            name="vacancyFormFiles"
                            uploadFieldText="Of sleep je bestand(en) hiernaartoe."
                            buttonText="Kies bestand(en)"
                            onChange={value =>
                                setValue("vacancyFormFiles", value)
                            }
                            currentFiles={watch("vacancyFormFiles")}
                            maxFileSize={20}
                            allowedFileTypes={[
                                "application/msword",
                                "application/pdf",
                                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            ]}
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            errorMessage={errors.vacancyFormFiles?.message}
                        />
                        <StyledTextArea
                            label="Bericht"
                            onChange={e =>
                                setValue(
                                    "message",
                                    // eslint-disable-next-line
                                    // @ts-ignore
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                                    e.target.value
                                )
                            }
                            placeholder="Eventuele toelichting, motivatie, etc.."
                        />
                    </FormInputsWrapper>

                    <FixedButtonWrapper>
                        <RequiredText>{t("requiredFields")}</RequiredText>
                        <Buttons>
                            {closeModal && isMobileView && (
                                <IconWrapper
                                    onClick={() => closeModal()}
                                    isMobileView={isMobileView}
                                >
                                    <Icon
                                        icon={CLOSE}
                                        color="var(--color-white)"
                                        size={isMobileView ? 16 : 24}
                                    />
                                </IconWrapper>
                            )}

                            <StyledSubmitButton
                                buttonType="primary"
                                icon={ARROW_RIGHT}
                                reversedOrder
                                disabled={isFormLoading || submitting}
                            >
                                {submitting
                                    ? "Aan het versturen..."
                                    : "Sollicitatie versturen"}
                            </StyledSubmitButton>
                        </Buttons>
                    </FixedButtonWrapper>
                </form>
            )}
        </StyledVacancyForm>
    );
}

export default VacancyForm;
